import {
  useState,
  useEffect,
  useRef,
  type Dispatch,
  type SetStateAction
} from "react";
import { useTranslation } from "react-i18next";
import * as amplitude from "@amplitude/analytics-browser";
import { SkeletonBase, TabsGroup } from "@unified-checkout/design-system";
import type { IPreDefinedRange } from "libs/design-system/src/types/date_range_picker";
import { TRANSACTIONS_EVENTS, TRANSACTIONS_EVENTS_PARAMS } from "@app/dashboard/constants/tracking_events/transactions";
import type { ITransactionsDateRange, ITransactionsStatusFilter } from "@app/dashboard/types/innerLayout/transactions";
import type { TAppliedRanges } from "@app/dashboard/types/innerLayout/date-ranges";
import DateRanges from "@app/dashboard/components/inner_layout/DateRanges";
import { transactionsStatusFilters } from "@app/dashboard/services/inner_layout/transactions/transactions-status-filters";
import { useScrollListener } from "@app/dashboard/hooks/useScrollListener";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import {
  mainPreDefinedRanges as preDefinedRanges,
  comparePreDefinedRanges
} from "@app/dashboard/utils/dateRangePicker";
import { getLang } from "@app/dashboard/utils/localStorageUtils";
import { formatToISODate } from "@app/dashboard/utils/dateFormats";
import styles from "./header.module.scss";

export const Header = ({
  setDateRange,
  setAppliedRangeText,
  setRangeSelected,
  appliedTransactionsStatus,
  setAppliedTransactionsStatus
}:
  {
    setDateRange: Dispatch<SetStateAction<ITransactionsDateRange>>,
    setAppliedRangeText: Dispatch<SetStateAction<IPreDefinedRange['label']>>,
    setRangeSelected: Dispatch<SetStateAction<IPreDefinedRange['label']>>,
    appliedTransactionsStatus: ITransactionsStatusFilter['name'],
    setAppliedTransactionsStatus: Dispatch<ITransactionsStatusFilter['name']>
  }) => {
  const { t, i18n } = useTranslation();
  const screenWidth = useScreenWidth();
  const lang = getLang();

  const tabsRef = useRef<any>();
  const wrapperRef = useRef<any>();

  const [appliedRanges, setAppliedRanges] = useState<TAppliedRanges>({});

  const [tabsGroupHasOverlay, setTabsGroupHasOverlay] = useState(false);

  const { start: startDate, end: endDate, index: rangeIndex, name: rangeName } = appliedRanges;
  const comparedPreDefinedRangeLabel: IPreDefinedRange['label'] | undefined = comparePreDefinedRanges(t)?.find(range => range.index === rangeIndex)?.label || '';

  const tEn = i18n.getFixedT('en');
  const preDefinedRangeEnLabel: IPreDefinedRange['label'] | undefined = preDefinedRanges(tEn)?.find(range => range.index === rangeIndex)?.label || '';

  useEffect(() => {
    if (startDate && endDate) {
      const formattedDateRange = formattedDates([startDate, endDate] as [Date, Date]);
      setDateRange(formattedDateRange);
    };

    setAppliedRangeText(comparedPreDefinedRangeLabel);
    setRangeSelected(preDefinedRangeEnLabel);
  }, [appliedRanges, lang]);

  useEffect(() => {
    handleTabsFading();
  }, [screenWidth]);

  const formattedDates = (range: [Date, Date]): ITransactionsDateRange => {
    const [formattedStartDate, formattedEndDate] = (range as [Date, Date])?.map((date) => (formatToISODate(date)));

    return { start_date: formattedStartDate, end_date: formattedEndDate };
  };

  const handleTabsFading = () => {
    const headerWidth = wrapperRef?.current?.clientWidth;
    const tabsWidth = tabsRef?.current?.scrollWidth;
    const tabsScrollX = Math.abs(tabsRef?.current?.scrollLeft);
    const smallOffset = 5;

    const reachedToTheEnd = ((tabsWidth - tabsScrollX) <= (headerWidth + smallOffset));

    setTabsGroupHasOverlay((tabsWidth > headerWidth) && !reachedToTheEnd);
  };

  const handleTabClick = ({
    tabLabel,
    tabName,
    tabId
  }: {
    tabLabel?: string,
    tabName?: ITransactionsStatusFilter['name'],
    tabId: string
  }) => {
    tabName && setAppliedTransactionsStatus(tabName);

    tabId && amplitude.track(TRANSACTIONS_EVENTS.TRNX_TYPE_SELECTED, {
      [TRANSACTIONS_EVENTS_PARAMS.TAB_NAME]: tabId
    });
  };

  useScrollListener(handleTabsFading, tabsRef);

  return (
    <div className={styles["header"]} ref={wrapperRef}>

      <div className={styles["header__title"]}>
        {t('innerLayout.navbar.transactions')}
      </div>

      <DateRanges
        appliedRanges={appliedRanges}
        handleRangesChange={(ranges) => setAppliedRanges(ranges)}
        lang={lang}
      />

      {
        false ?
          <SkeletonBase height="2.6875rem" />
          :
          <div className={`${styles["header__tabs"]} ${tabsGroupHasOverlay ? styles["header__tabs--faded"] : ''}`}>
            <div className={styles["header__tabs__group-wrapper"]} ref={tabsRef}>
              <TabsGroup
                type="line"
                orientation="horizontal"
                tabsProps={
                  transactionsStatusFilters(t).map((filterTab) => ({
                    active: appliedTransactionsStatus === filterTab.name,
                    disabled: false,
                    tabName: filterTab.name,
                    tabLabel: filterTab.label,
                    onClickHandler: () => handleTabClick({
                      tabLabel: filterTab.label,
                      tabName: filterTab.name,
                      tabId: filterTab.id
                    }),
                  }
                  ))
                }
              />
            </div>
          </div>
      }

    </div>
  )
};